import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection, query, where, orderBy, onSnapshot, Timestamp, limit,
} from 'firebase/firestore';
import { startOfDay, endOfDay, isToday } from 'date-fns';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from '../context/AuthContext';
import {
  mergeReplayEvents,
  filterReplayEvents,
  computeReplaySummary,
  detectActivityGaps,
  flagLongRunningEvents,
  computeHighCostThreshold,
  buildReplayInsights,
} from '../utils/productionReplay';
import {
  buildLiveTaskViews,
  enrichEventsWithLiveState,
  isReplayLiveMode,
} from '../utils/replayLive';
import { PRODUCTION_EVENT_TYPES } from '../utils/productionEvents';

const RECENT_TASKS_LIMIT = 600;
const NOTES_LIMIT = 500;
const LOGS_LIMIT = 200;

function toTs(date) {
  return Timestamp.fromDate(date);
}

function toDate(raw) {
  if (!raw) return null;
  const d = raw instanceof Date ? raw : raw.toDate?.() ?? new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function inDayRange(raw, dayStart, dayEnd) {
  const d = toDate(raw);
  return d != null && d >= dayStart && d <= dayEnd;
}

function taskOverlapsDay(task, dayStart, dayEnd) {
  const start = toDate(task.startTime ?? task.createdAt);
  if (!start) return false;
  const end = toDate(task.submittedAt ?? task.endTime);
  if (start > dayEnd) return false;
  if (end && end < dayStart) return false;
  return true;
}

function taskRelevantToDay(task, dayStart, dayEnd) {
  if (inDayRange(task.startTime ?? task.createdAt, dayStart, dayEnd)) return true;
  if (inDayRange(task.submittedAt ?? task.endTime, dayStart, dayEnd)) return true;
  if (task.status === 'abandoned' && inDayRange(task.editedAt ?? task.updatedAt, dayStart, dayEnd)) {
    return true;
  }
  if (task.status === 'active' && taskOverlapsDay(task, dayStart, dayEnd)) return true;
  return false;
}

const LISTENER_IDS = ['events', 'completed', 'recent', 'active', 'notes', 'logs'];

export function useProductionReplay(selectedDate, filters, benchmarks, labourContext = {}) {
  const { i18n } = useTranslation();
  const { userRates = {}, defaultTeamRate = null } = labourContext;
  const { companyId } = useAuth();
  const [loggedEvents, setLoggedEvents] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [recentTasks, setRecentTasks] = useState([]);
  const [activeTasks, setActiveTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventsUnavailable, setEventsUnavailable] = useState(false);
  const [liveTick, setLiveTick] = useState(() => Date.now());
  const readyRef = useRef(new Set());
  const errorsRef = useRef([]);

  const dayRange = useMemo(() => {
    const d = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
    return { start: startOfDay(d), end: endOfDay(d) };
  }, [selectedDate]);

  const includeActiveQuery = useMemo(
    () => isToday(selectedDate instanceof Date ? selectedDate : new Date(selectedDate)),
    [selectedDate],
  );

  useEffect(() => {
    if (!companyId) { setLoading(false); return undefined; }

    readyRef.current = new Set();
    errorsRef.current = [];
    setLoading(true);
    setError(null);
    setEventsUnavailable(false);

    const expectedListeners = includeActiveQuery
      ? LISTENER_IDS
      : LISTENER_IDS.filter((id) => id !== 'active');

    const markReady = (id, err) => {
      if (err) {
        console.warn(`[useProductionReplay:${id}]`, err?.message || err);
        errorsRef.current.push(id);
      }
      readyRef.current.add(id);
      if (readyRef.current.size >= expectedListeners.length) {
        setLoading(false);
        const tasksFailed = ['completed', 'recent'].every(
          (k) => errorsRef.current.includes(k),
        );
        setError(tasksFailed ? i18n.t('replayExtra.loadError') : null);
      }
    };

    const startTs = toTs(dayRange.start);
    const endTs = toTs(dayRange.end);

    // Optional — requires productionEvents rules deployed. Fail soft.
    const unsubEvents = onSnapshot(
      query(
        collection(db, COLLECTIONS.PRODUCTION_EVENTS),
        where('companyId', '==', companyId),
        where('timestamp', '>=', startTs),
        where('timestamp', '<=', endTs),
        orderBy('timestamp', 'asc'),
      ),
      (snap) => {
        setLoggedEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        markReady('events');
      },
      (err) => {
        setLoggedEvents([]);
        setEventsUnavailable(true);
        markReady('events', err);
      },
    );

    // Uses existing index: companyId + status + archived + submittedAt (desc)
    const unsubCompleted = onSnapshot(
      query(
        collection(db, COLLECTIONS.PRODUCTION_TASKS),
        where('companyId', '==', companyId),
        where('status', '==', 'completed'),
        where('archived', '==', false),
        where('submittedAt', '>=', startTs),
        where('submittedAt', '<=', endTs),
        orderBy('submittedAt', 'desc'),
      ),
      (snap) => {
        setCompletedTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        markReady('completed');
      },
      (err) => { setCompletedTasks([]); markReady('completed', err); },
    );

    // Uses existing index: companyId + archived + createdAt (desc)
    const unsubRecent = onSnapshot(
      query(
        collection(db, COLLECTIONS.PRODUCTION_TASKS),
        where('companyId', '==', companyId),
        where('archived', '==', false),
        orderBy('createdAt', 'desc'),
        limit(RECENT_TASKS_LIMIT),
      ),
      (snap) => {
        setRecentTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        markReady('recent');
      },
      (err) => { setRecentTasks([]); markReady('recent', err); },
    );

    let unsubActive = () => {};
    if (includeActiveQuery) {
      // Uses existing index: companyId + status + archived + startTime (desc)
      unsubActive = onSnapshot(
        query(
          collection(db, COLLECTIONS.PRODUCTION_TASKS),
          where('companyId', '==', companyId),
          where('status', '==', 'active'),
          where('archived', '==', false),
          orderBy('startTime', 'desc'),
        ),
        (snap) => {
          const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setActiveTasks(all.filter((t) => taskOverlapsDay(t, dayRange.start, dayRange.end)));
          markReady('active');
        },
        (err) => { setActiveTasks([]); markReady('active', err); },
      );
    } else {
      setActiveTasks([]);
      markReady('active');
    }

    // Uses existing index: companyId + archived + createdAt (desc)
    const unsubNotes = onSnapshot(
      query(
        collection(db, COLLECTIONS.TASK_NOTES),
        where('companyId', '==', companyId),
        where('archived', '==', false),
        orderBy('createdAt', 'desc'),
        limit(NOTES_LIMIT),
      ),
      (snap) => {
        setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        markReady('notes');
      },
      (err) => { setNotes([]); markReady('notes', err); },
    );

    // Uses existing index: companyId + entityType + createdAt (desc)
    const unsubLogs = onSnapshot(
      query(
        collection(db, COLLECTIONS.ACTIVITY_LOGS),
        where('companyId', '==', companyId),
        where('entityType', '==', 'productionTask'),
        orderBy('createdAt', 'desc'),
        limit(LOGS_LIMIT),
      ),
      (snap) => {
        setActivityLogs(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((log) => log.action === 'edit_task'),
        );
        markReady('logs');
      },
      (err) => { setActivityLogs([]); markReady('logs', err); },
    );

    return () => {
      unsubEvents();
      unsubCompleted();
      unsubRecent();
      unsubActive();
      unsubNotes();
      unsubLogs();
    };
  }, [companyId, dayRange.start.getTime(), dayRange.end.getTime(), includeActiveQuery]);

  const dayNotes = useMemo(
    () => notes.filter((n) => inDayRange(n.createdAt, dayRange.start, dayRange.end)),
    [notes, dayRange],
  );

  const dayLogs = useMemo(
    () => activityLogs.filter((l) => inDayRange(l.createdAt, dayRange.start, dayRange.end)),
    [activityLogs, dayRange],
  );

  const tasks = useMemo(() => {
    const map = new Map();
    for (const t of completedTasks) map.set(t.id, t);
    for (const t of recentTasks.filter((task) => taskRelevantToDay(task, dayRange.start, dayRange.end))) {
      map.set(t.id, t);
    }
    for (const t of activeTasks) map.set(t.id, t);
    return [...map.values()];
  }, [completedTasks, recentTasks, activeTasks, dayRange]);

  const tasksById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);

  const highCostThreshold = useMemo(
    () => computeHighCostThreshold(tasks),
    [tasks],
  );

  const allEvents = useMemo(() => mergeReplayEvents({
    loggedEvents,
    tasks,
    notes: dayNotes,
    activityLogs: dayLogs,
    dayStart: dayRange.start,
    dayEnd: dayRange.end,
  }), [loggedEvents, tasks, dayNotes, dayLogs, dayRange]);

  useEffect(() => {
    if (!includeActiveQuery || activeTasks.length === 0) return undefined;
    const id = setInterval(() => setLiveTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [includeActiveQuery, activeTasks.length]);

  const liveAsOf = useMemo(() => new Date(liveTick), [liveTick]);

  const liveTasks = useMemo(() => {
    if (!includeActiveQuery || activeTasks.length === 0) return [];
    return buildLiveTaskViews({
      activeTasks,
      events: allEvents,
      benchmarks,
      userRates,
      defaultTeamRate,
      currency: filters.currency,
      asOf: liveAsOf,
    });
  }, [includeActiveQuery, activeTasks, allEvents, benchmarks, userRates, defaultTeamRate, filters.currency, liveAsOf, i18n.language]);

  const liveTasksById = useMemo(
    () => new Map(liveTasks.map((t) => [t.id, t])),
    [liveTasks],
  );

  const needsAttentionTaskIds = useMemo(
    () => new Set(liveTasks.filter((t) => t.needsAttention).map((t) => t.id)),
    [liveTasks],
  );

  const isLiveMode = isReplayLiveMode(selectedDate, activeTasks.length);

  const filteredEvents = useMemo(
    () => filterReplayEvents(allEvents, {
      ...filters,
      highCostThreshold,
      needsAttentionTaskIds: filters.needsAttention ? needsAttentionTaskIds : null,
    }, tasksById),
    [allEvents, filters, tasksById, highCostThreshold, needsAttentionTaskIds],
  );

  const highlightedEvents = useMemo(() => {
    let result = flagLongRunningEvents(filteredEvents, benchmarks, tasksById);
    result = enrichEventsWithLiveState(result, liveTasksById, tasksById);
    if (filters.longRunsOnly) {
      result = result.filter(
        (e) => e.highlight === 'long-running'
          || (e.eventType === PRODUCTION_EVENT_TYPES.TASK_PAUSED
            && (e.durationMinutes ?? 0) >= 30
            && !e.metadata?.syntheticPauseSummary),
      );
    }
    return result;
  }, [filteredEvents, benchmarks, tasksById, liveTasksById, filters.longRunsOnly]);

  const gaps = useMemo(
    () => detectActivityGaps(filteredEvents),
    [filteredEvents],
  );

  const summary = useMemo(
    () => computeReplaySummary(
      allEvents,
      tasks,
      dayRange.start,
      dayRange.end,
      filters.currency,
      { asOf: liveAsOf, includeActiveElapsed: includeActiveQuery },
    ),
    [allEvents, tasks, dayRange, filters.currency, liveAsOf, includeActiveQuery, i18n.language],
  );

  const insights = useMemo(() => {
    const longPauses = filteredEvents.filter(
      (e) => e.eventType === PRODUCTION_EVENT_TYPES.TASK_PAUSED
        && (e.durationMinutes ?? 0) >= 30
        && !e.metadata?.syntheticPauseSummary,
    );
    const materialDelays = filteredEvents.filter(
      (e) => e.eventType === PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED,
    );
    const startedCount = filteredEvents.filter(
      (e) => e.eventType === PRODUCTION_EVENT_TYPES.TASK_STARTED,
    ).length;

    const replayInsights = buildReplayInsights({
      gaps,
      events: highlightedEvents,
      activeTasks: liveTasks,
    });

    const needsAttentionTasks = liveTasks.filter((t) => t.needsAttention);

    return {
      longPauses,
      materialDelays,
      needsAttentionTasks,
      replayInsights,
      overlaps: startedCount > 1,
      gaps,
    };
  }, [filteredEvents, gaps, highlightedEvents, liveTasks, i18n.language]);

  return {
    events: highlightedEvents,
    allEvents,
    tasks,
    summary,
    insights,
    liveTasks,
    liveTasksById,
    isLiveMode,
    loading,
    error,
    eventsUnavailable,
    dayRange,
  };
}
